import type { BandScale, LinearScale } from './scale';
import type { Datum, Series } from './types';

/**
 * Turning readings into path data.
 *
 * Pure and JSX-free for the same reason as `scale.ts`: this is where a chart
 * is right or wrong, and it has to be testable in a node environment.
 */

interface Point {
  x: number;
  y: number;
  datum: Datum;
}

/**
 * Positions for one series, with gaps preserved.
 *
 * A `null` reading yields no point at all rather than a point at zero. That
 * distinction is the whole reason `Datum.value` is nullable: "the lab was shut"
 * and "the lab did nothing" are different facts, and a line that dives to the
 * baseline on a public holiday invents the second one.
 */
export function pointsFor(
  points: readonly Datum[],
  x: BandScale,
  y: LinearScale,
): (Point | null)[] {
  return points.map((datum) =>
    datum.value === null ? null : { x: x.centre(datum.key), y: y(datum.value), datum },
  );
}

/**
 * An SVG path for a line, broken at every gap.
 *
 * One path with `M` restarts rather than one path per run: fewer nodes, and a
 * single element to carry the series' stroke and dash.
 */
export function linePath(points: readonly (Point | null)[]): string {
  let out = '';
  let pen = false;

  for (const point of points) {
    if (point === null) {
      pen = false;
      continue;
    }
    out += `${pen ? 'L' : 'M'} ${round(point.x)} ${round(point.y)} `;
    pen = true;
  }
  return out.trim();
}

/**
 * A filled area under a line, one closed shape per unbroken run.
 *
 * Bridging a gap would shade a region the data says nothing about, so each run
 * closes to the baseline on its own.
 */
export function areaPath(points: readonly (Point | null)[], baseline: number): string {
  const runs: Point[][] = [];
  let run: Point[] = [];

  for (const point of points) {
    if (point === null) {
      if (run.length > 0) runs.push(run);
      run = [];
    } else {
      run.push(point);
    }
  }
  if (run.length > 0) runs.push(run);

  return (
    runs
      // A single point has no area. Drawing it as a zero-width sliver puts a
      // 1px artefact on the baseline that reads as a data point.
      .filter((points) => points.length > 1)
      .map((points) => {
        const first = points[0]!;
        const last = points[points.length - 1]!;
        const top = points.map((point) => `L ${round(point.x)} ${round(point.y)}`).join(' ');
        return `M ${round(first.x)} ${round(baseline)} ${top} L ${round(last.x)} ${round(baseline)} Z`;
      })
      .join(' ')
  );
}

export interface StackSegment {
  seriesId: string;
  seriesIndex: number;
  datum: Datum;
  /** Value-space bounds, before scaling. */
  from: number;
  to: number;
}

/**
 * Stacked segments per category, bottom-up in series order.
 *
 * `null` contributes nothing and does not break the stack — in a stacked chart
 * a missing component means "none of this kind", which is genuinely zero for
 * the purpose of the total, unlike a missing point on a line.
 *
 * Negative values are not stacked. Mixing signs in one stack produces a bar
 * whose height is neither the total nor the range, and no reader can tell which
 * — so callers get the segments back unstacked and the chart refuses to
 * normalise. Guarded by `stackIsSound` rather than silently clamped.
 */
export function stackFor(
  seriesList: readonly Series[],
  keys: readonly string[],
): Map<string, StackSegment[]> {
  const byKey = new Map<string, StackSegment[]>();

  for (const key of keys) {
    let cursor = 0;
    const segments: StackSegment[] = [];

    seriesList.forEach((series, seriesIndex) => {
      const datum = series.points.find((point) => point.key === key);
      if (!datum || datum.value === null) return;

      segments.push({
        seriesId: series.id,
        seriesIndex,
        datum,
        from: cursor,
        to: cursor + datum.value,
      });
      cursor += datum.value;
    });

    byKey.set(key, segments);
  }
  return byKey;
}

/** Every value in a stack must share a sign for the total to mean anything. */
export function stackIsSound(seriesList: readonly Series[]): boolean {
  const values = seriesList.flatMap((series) =>
    series.points.map((point) => point.value).filter((value): value is number => value !== null),
  );
  return !values.some((value) => value < 0);
}

/**
 * The value-space extent a chart must cover.
 *
 * `includeZero` is not optional for bars, and the caller cannot turn it off —
 * see `BarChart`. A bar cut off at 40 rather than 0 misstates the ratio between
 * two bars, which is the single most common way a chart lies.
 */
export function extentOf(
  seriesList: readonly Series[],
  { stacked = false, includeZero = true } = {},
): [number, number] {
  const totals: number[] = [];

  if (stacked) {
    const keys = new Set(seriesList.flatMap((series) => series.points.map((point) => point.key)));
    for (const key of keys) {
      let sum = 0;
      for (const series of seriesList) {
        const datum = series.points.find((point) => point.key === key);
        if (datum?.value != null) sum += datum.value;
      }
      totals.push(sum);
    }
  } else {
    for (const series of seriesList) {
      for (const point of series.points) if (point.value !== null) totals.push(point.value);
    }
  }

  if (totals.length === 0) return [0, 1];

  const min = Math.min(...totals);
  const max = Math.max(...totals);
  return includeZero ? [Math.min(0, min), Math.max(0, max)] : [min, max];
}

/**
 * A line chart's value extent: the readings, with zero when the axis is
 * anchored there, stretched to take in a reference rule when there is one.
 */
export function valueExtentFor(
  seriesList: readonly Series[],
  includeZero: boolean,
  reference?: number,
): [number, number] {
  const [min, max] = extentOf(seriesList, { includeZero });
  return reference === undefined
    ? [min, max]
    : [Math.min(min, reference), Math.max(max, reference)];
}

/**
 * Arcs for a donut, as `[startAngle, endAngle]` in radians from twelve o'clock.
 *
 * Zero and negative slices are dropped rather than drawn as hairlines: a
 * zero-width wedge is an invisible legend entry and an unclickable target.
 */
interface Arc {
  datum: Datum;
  index: number;
  start: number;
  end: number;
  share: number;
}

export function arcsFor(points: readonly Datum[]): Arc[] {
  const usable = points
    .map((datum, index) => ({ datum, index }))
    .filter(({ datum }) => datum.value !== null && datum.value > 0);

  const total = usable.reduce((sum, { datum }) => sum + (datum.value ?? 0), 0);
  if (total === 0) return [];

  let cursor = -Math.PI / 2;
  return usable.map(({ datum, index }) => {
    const share = (datum.value ?? 0) / total;
    const start = cursor;
    cursor += share * Math.PI * 2;
    return { datum, index, start, end: cursor, share };
  });
}

/** An SVG path for one ring segment. */
export function arcPath(arc: Arc, outer: number, inner: number): string {
  const large = arc.end - arc.start > Math.PI ? 1 : 0;
  const p = (radius: number, angle: number) =>
    `${round(Math.cos(angle) * radius)} ${round(Math.sin(angle) * radius)}`;

  // A full circle cannot be drawn as a single arc — start and end coincide and
  // the renderer draws nothing. Split it in half.
  if (arc.end - arc.start >= Math.PI * 2 - 1e-9) {
    const mid = arc.start + Math.PI;
    return (
      `M ${p(outer, arc.start)} A ${outer} ${outer} 0 1 1 ${p(outer, mid)} ` +
      `A ${outer} ${outer} 0 1 1 ${p(outer, arc.start)} ` +
      `M ${p(inner, arc.start)} A ${inner} ${inner} 0 1 0 ${p(inner, mid)} ` +
      `A ${inner} ${inner} 0 1 0 ${p(inner, arc.start)} Z`
    );
  }

  return (
    `M ${p(outer, arc.start)} A ${outer} ${outer} 0 ${large} 1 ${p(outer, arc.end)} ` +
    `L ${p(inner, arc.end)} A ${inner} ${inner} 0 ${large} 0 ${p(inner, arc.start)} Z`
  );
}

interface BarSpan {
  horizontal: boolean;
  /** Where the bar's band starts on the category axis, and how wide it is. */
  slot: number;
  band: number;
  /** Scaled value-axis ends, in either order. */
  from: number;
  to: number;
}

/** The rectangle for one bar, whichever way the chart runs. */
export function barRect({ horizontal, slot, band, from, to }: BarSpan): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const start = Math.min(from, to);
  const length = Math.abs(to - from);
  return horizontal
    ? { x: start, y: slot, width: length, height: band }
    : { x: slot, y: start, width: band, height: length };
}

/** Two decimals is well under a device pixel and keeps the DOM small. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
